import {
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { languages, type Language } from '@shared/models/auth';
import { map } from 'rxjs';

/**

 */
@Component({
  selector: 'zyn-footer',
  template: `
    @if (showBranding()) {
      <p
        class="text-sm flex items-center justify-center gap-1"
        [innerHTML]="'footer.branding' | translate"
      ></p>
    }
    <div class="flex items-center justify-center gap-6 text-xs flex-wrap">
      @for (link of legalLinks; track link.href) {
        <a
          [href]="link.href"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:text-primary transition-colors"
        >
          {{ link.key | translate }}
        </a>
      }
    </div>
    <span class="flex items-center justify-self-center text-xs gap-1">
      @for (lang of toggleLangs; track lang; let last = $last) {
        <button
          type="button"
          class="underline text-xs disabled:opacity-100 disabled:font-semibold disabled:no-underline"
          [disabled]="currentLang() === lang"
          (click)="setLang(lang)"
        >
          {{ lang.toUpperCase() }}
        </button>
        @if (!last) {
          <span aria-hidden="true">/</span>
        }
      }
    </span>
  `,
  imports: [TranslatePipe],
  host: {
    class: 'text-center grid gap-4 pb-4',
  },
})
export class Footer {
  readonly showBranding = input(true);

  /** External legal links, in display order. */
  readonly legalLinks = [
    { key: 'footer.imprint', href: 'https://zynday.com/imprint' },
    { key: 'footer.terms', href: 'https://zynday.com/terms' },
    { key: 'footer.privacy', href: 'https://zynday.com/privacy' },
  ] as const;

  /** Languages offered by the toggle, in display order. */
  readonly toggleLangs = languages;

  readonly #translate = inject(TranslateService);

  readonly #lang = toSignal(
    this.#translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this.#activeLang() },
  );
  readonly currentLang = computed(() => (this.#lang() || 'en').toLowerCase());

  setLang(lang: Language): void {
    if (this.currentLang() === lang) return;
    this.#translate.use(lang);
  }

  /** Best-known active language to seed the toggle before the first change. */
  #activeLang(): string {
    return (
      this.#translate.getCurrentLang() ||
      this.#translate.getFallbackLang() ||
      'en'
    );
  }
}
